// frontend/services/PayService.js

import axiosInstance from './axiosInstance';
// import axiosBaseInstance from './axiosBaseInstance';
import { formatDate } from '../utils/dateUtils';
import { debugLog, debugSensitive, logError } from '../utils/debugUtils';

// Simple cache for pay bank accounts to prevent redundant API calls
const payBankAccountsCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCachedBankAccounts(payId) {
  const cached = payBankAccountsCache.get(payId);
  if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
    return cached.data;
  }
  return null;
}

function setCachedBankAccounts(payId, data) {
  payBankAccountsCache.set(payId, {
    data,
    timestamp: Date.now()
  });
}

export async function getPayBankAccount(payId) {
  // request data validation
  if (!payId || typeof payId !== 'number') {
    throw new Error('Invalid pay ID');
  }

  try {
    const response = await axiosInstance.get(`/pays/${encodeURIComponent(payId)}/bank-accounts/`);
        if (!Array.isArray(response.data)) {
      throw new Error('Pay bank accounts data must be an array');
    }
    // response data validation
    const validBankAccounts = response.data.every(account => (
      typeof account.bank_account === 'number' &&
      typeof account.transfer_amount === 'string' &&
      typeof account.transfer_made === 'boolean'
    ));

    if (!validBankAccounts) {
      throw new Error('Invalid pay bank account data structure');
    }

    return response.data;
  } catch (error) {
    logError('fetching PayBankAccount data', error);
    throw error;
  }
}

export async function getTrackedPayBankAccount(payId, bankAccountId) {
// request data validation
  if (!payId || typeof payId !== 'number') {
    throw new Error('Invalid pay ID');
  }
  if (!bankAccountId || typeof bankAccountId !== 'number') {
    throw new Error('Invalid bank account ID');
  }

  try {
    const response = await axiosInstance.get(
      `/pays/${encodeURIComponent(payId)}/bank-accounts/${encodeURIComponent(bankAccountId)}/`
    );
    // response data validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid tracked pay bank account data');
    }

    const validData = (
      typeof response.data.bank_account === 'number' &&
      typeof response.data.transfer_amount === 'string' &&
      typeof response.data.transfer_made === 'boolean'
    );

    if (!validData) {
      throw new Error('Invalid tracked pay bank account structure');
    }

    return response.data;
  } catch (error) {
    logError('fetching Tracked PayBankAccount data', error);
    throw error;
  }
}

export async function getFuturePaySeries() {
  try {
    const response = await axiosInstance.get('/pays/future-series/');
    // response data validation
    if (!Array.isArray(response.data)) {
      throw new Error('Pay series data must be an array');
    }

    const validSeries = response.data.every((series) => {
      const idNum = Number(series.id);
      return (
        Number.isFinite(idNum) &&
        typeof series.nickname === 'string' &&
        typeof series.amount === 'string' &&
        typeof series.next_pay === 'string' &&
        !isNaN(new Date(series.next_pay).getTime())
      );
    });

    if (!validSeries) {
      throw new Error('Invalid pay series data structure');
    }

    return response.data.map((s) => ({ ...s, id: Number(s.id) }));
  } catch (error) {
    logError('fetching PaySeries data', error);
    throw error;
  }
}

export async function getPaySeries() {
  try {
    const response = await axiosInstance.get(`/pays/series/`);
    // response data validation
    if (!Array.isArray(response.data)) {
      throw new Error('Pay series data must be an array');
    }

    const validSeries = response.data.every(series => (
      typeof series.id === 'number' &&
      typeof series.nickname === 'string' &&
      typeof series.amount === 'string' &&
      typeof series.next_pay === 'string' &&
      !isNaN(new Date(series.next_pay).getTime())
    ));

    if (!validSeries) {
      throw new Error('Invalid pay series data structure');
    }


    return response.data;
  } catch (error) {
    logError('fetching PaySeries data', error);
    throw error;
  }
}

export async function getPays(filterDate) {
  try {
    const response = await axiosInstance.get('/pays/');

    // Validate initial response structure
    if (!Array.isArray(response.data)) {
      throw new Error('Pays data must be an array');
    }

    // Validate each pay object
    const validPays = response.data.every(pay => (
      typeof pay.id === 'number' &&
      typeof pay.nickname === 'string' &&
      typeof pay.amount === 'string' &&
      typeof pay.next_pay === 'string'
    ));

    if (!validPays) {
      throw new Error('Invalid pay data structure');
    }

    let pays = response.data;

    if (filterDate) {
      pays = pays.filter((pay) => new Date(pay.due_date) >= filterDate);

      pays = await Promise.all(pays.map(async (pay) => {
        const bankAccountsResponse = await axiosInstance.get(`/pays/${encodeURIComponent(pay.id)}/bank-accounts/`);
        const bankAccounts = bankAccountsResponse.data;
        return { ...pay, bank_accounts: bankAccounts };
      }));
    }

    return pays;
  } catch (error) {
    logError('fetching pays', error);
    throw error;
  }
}

export async function addPay(newPay) {
  // Input validation
  if (!newPay || typeof newPay !== 'object') {
    throw new Error('Invalid pay data');
  }
  if (typeof newPay.nickname !== 'string' || !newPay.nickname.trim()) {
    throw new Error('Invalid pay name');
  }
  if (typeof newPay.amount !== 'string' || isNaN(parseFloat(newPay.amount))) {
    throw new Error('Invalid amount');
  }
  if (!newPay.next_pay || isNaN(new Date(newPay.next_pay).getTime())) {
    throw new Error('Invalid next pay date');
  }

  try {
    const response = await axiosInstance.post('/pays/', newPay);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (!response.data.id || typeof response.data.nickname !== 'string') {
      throw new Error('Invalid pay response structure');
    }

    return response.data;
  } catch (error) {
    logError('adding pay', error);
    throw error;
  }
}

export async function updatePay(payId, payAmount, paySeriesId = null) {
  // Input validation
  if (!payId || typeof payId !== 'number') {
    throw new Error('Invalid pay ID');
  }
  if (typeof payAmount !== 'string' || isNaN(parseFloat(payAmount))) {
    throw new Error('Invalid pay amount');
  }
  if (paySeriesId !== null && typeof paySeriesId !== 'string' && typeof paySeriesId !== 'number') {
    throw new Error('Invalid pay series ID');
  }

  const data = {
    amount: payAmount,
    ...(paySeriesId && { paySeriesId: paySeriesId })  // Only include paySeriesId if it exists
  };

  try {
    const response = await axiosInstance.put(`/pays/${encodeURIComponent(payId)}/`, data);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }

    // Force a recalculation of unallocated amounts if any exist
    try {
      // Explicitly call the backend endpoint to recalculate
      await axiosInstance.get(`calculate-unallocated-amount/${encodeURIComponent(payId)}/`);
      
      // If updating a series, try to get other pays in the series to recalculate their unallocated amounts too
      if (paySeriesId) {
        try {
          // Try to get pays in the same series (gracefully handle failure)
          const seriesResponse = await axiosInstance.get(`pays/series/${encodeURIComponent(payId)}`);
          if (seriesResponse.data && Array.isArray(seriesResponse.data)) {
            // For each pay in the series, trigger recalculation
            for (const seriesPay of seriesResponse.data) {
              if (seriesPay.id !== payId) {
                await axiosInstance.get(`calculate-unallocated-amount/${encodeURIComponent(seriesPay.id)}/`);
              }
            }
          }
        } catch (seriesError) {
          debugLog('Unable to recalculate for series pays');
        }
      }
    } catch (error) {
      debugLog('Error recalculating unallocated amount:', error.name);
      // Continue even if this fails, as the pay itself was updated successfully
    }

    return response.data;
  } catch (error) {
    logError('updating pay', error);
    throw error;
  }
}


export async function deletePay(payId) {
  // Input validation
  if (!payId || typeof payId !== 'number') {
    throw new Error('Invalid pay ID');
  }

  try {
    await axiosInstance.delete(`/pays/${encodeURIComponent(payId)}/`);
    return true;
  } catch (error) {
    logError('deleting pay', error);
    throw error;
  }
}


export async function updatePayBankAccount(payId, bankAccountId, transfer_option, transfer_amount, transfer_made) {
  // Input validation
  if (!payId || typeof payId !== 'number') {
    throw new Error('Invalid pay ID');
  }
  if (!bankAccountId || typeof bankAccountId !== 'number') {
    throw new Error('Invalid bank account ID');
  }
  if (!['fixed', 'remaining', 'unallocated'].includes(transfer_option)) {
    throw new Error('Invalid transfer option');
  }

  const data = {
    bank_account_id: bankAccountId,
    transfer_option: transfer_option,
    transfer_amount: transfer_amount,
    transfer_made: transfer_made,
  };

  try {
    const response = await axiosInstance.put(`/pays/${encodeURIComponent(payId)}/`, data);
    
    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (typeof response.data.pay_bank_account.transfer_amount !== 'string') {
      throw new Error('Invalid pay bank account response structure');
    }

    // After updating a pay bank account, trigger a recalculation of unallocated amounts
    // for this pay (if the transfer changed wasn't itself an unallocated transfer)
    if (transfer_option !== 'unallocated') {
      try {
        // Find the pay's fortnight dates to get all pays in the same fortnight
        const fortnightResponse = await axiosInstance.get(`calculate-unallocated-amount/${encodeURIComponent(payId)}/`);
      } catch (recalcError) {
        debugLog('[PayService] Error recalculating unallocated amounts');
        // Continue even if recalculation fails - the primary update succeeded
      }
    }

    return response.data;
  } catch (error) {
    logError('[PayService] updatePayBankAccount', error);
    throw error;
  }
}

export async function updatePayBankAccountTransferStatus(payId, bankAccountId, transfer_made) {

  // Input validation
  if (!payId || typeof payId !== 'number') {
    throw new Error('Invalid pay ID');
  }
  if (!bankAccountId || typeof bankAccountId !== 'number') {
    throw new Error('Invalid bank account ID');
  }
  if (typeof transfer_made !== 'boolean') {
    throw new Error('Invalid transfer made status');
  }

  const data = {
    bank_account_id: bankAccountId,
    transfer_made: transfer_made,
  };

  try {
    const response = await axiosInstance.put(
      `/pays/${encodeURIComponent(payId)}/bank-accounts/${encodeURIComponent(bankAccountId)}/`,
      data
    );
    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (typeof response.data.transfer_made !== 'boolean') {
      throw new Error('Invalid transfer status response structure');
    }

    return response.data;
  } catch (error) {
    logError('updating payBankAccount transfer status', error);
    throw error;
  }
}

export async function updatePayStatus(payId, paidStatus, paidDate, updatedPay) {
  // Input validation
  if (!payId || typeof payId !== 'number') {
    throw new Error('Invalid pay ID');
  }
  if (typeof paidStatus !== 'boolean') {
    throw new Error('Invalid paid status');
  }
  if (paidDate && isNaN(new Date(paidDate).getTime())) {
    throw new Error('Invalid paid date');
  }
  if (!updatedPay || typeof updatedPay !== 'object') {
    throw new Error('Invalid pay data');
  }

  const data = {
    nickname: updatedPay.name,
    amount: updatedPay.amount,
    next_pay: updatedPay.next_pay,
    paid: updatedPay.paid,
  };

  try {
    const response = await axiosInstance.put(`/pays/${encodeURIComponent(payId)}/`, data);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }
    if (typeof response.data.paid !== 'boolean') {
      throw new Error('Invalid pay status response');
    }

    return response.data;
  } catch (error) {
    logError('updating pay status', error);
    throw error;
  }
}

export function filterPaysByWeek(pays, lastWeekStart, nextWeekEnd) {
  const formattedLastWeekStart = formatDate(lastWeekStart);
  const formattedNextWeekEnd = formatDate(nextWeekEnd);

  return pays.filter((pay) => {
    return (formattedLastWeekStart <= pay.due_date) && (pay.due_date <= formattedNextWeekEnd);
  }).sort((a, b) => new Date(a.next_pay) - new Date(b.next_pay));
}


export async function getAllPaysForSettings() {
  const response = await axiosInstance.get('/pays/');
  if (!Array.isArray(response.data)) {
    throw new Error('Pays data must be an array');
  }
  return response.data;
}

/** One representative pay per series for rows whose next_pay is before today (latest past date per series). */
export function collapsePastSeriesRepresentatives(pays, localIsoDateFromPay, todayStr) {
  const past = pays.filter((p) => localIsoDateFromPay(p.next_pay) < todayStr);
  const bySeries = new Map();
  for (const p of past) {
    const sid = String(p.pay_series_id);
    const cur = bySeries.get(sid);
    if (!cur || new Date(p.next_pay) > new Date(cur.next_pay)) {
      bySeries.set(sid, p);
    }
  }
  return Array.from(bySeries.values());
}

export async function previewExtendPaySeries(seriesId, body) {
  const response = await axiosInstance.post(
    `/pays/series/${encodeURIComponent(seriesId)}/extend/preview/`,
    body
  );
  return response.data;
}

export async function commitExtendPaySeries(seriesId, body) {
  const response = await axiosInstance.post(
    `/pays/series/${encodeURIComponent(seriesId)}/extend/`,
    body
  );
  return response.data;
}

export async function scopedUpdatePay(body) {
  const response = await axiosInstance.post('/pays/scoped-update/', body);
  return response.data;
}

export async function deleteScheduledPays(seriesId, body) {
  const response = await axiosInstance.post(
    `/pays/series/${encodeURIComponent(seriesId)}/delete-scheduled/`,
    body
  );
  return response.data;
}

export async function previewDeleteScheduledPays(seriesId, body) {
  const response = await axiosInstance.post(
    `/pays/series/${encodeURIComponent(seriesId)}/delete-scheduled/preview/`,
    body
  );
  return response.data;
}

export async function configurePaySeriesAutoMaintain(seriesId, minimumUpcoming) {
  const response = await axiosInstance.post(
    `/pays/series/${encodeURIComponent(seriesId)}/auto-maintain/`,
    { auto_maintain_min_upcoming: minimumUpcoming }
  );
  return response.data;
}

export async function fetchFilteredPays(startDate, endDate) {
  // Input validation
  if (startDate && isNaN(startDate.getTime())) {
    throw new Error('Invalid start date');
  }
  if (endDate && isNaN(endDate.getTime())) {
    throw new Error('Invalid end date');
  }

  try {
    const response = await axiosInstance.get('/pays/', {
      params: {
        start_date: startDate ? encodeURIComponent(startDate.toISOString().split('T')[0]) : null,
        end_date: endDate ? encodeURIComponent(endDate.toISOString().split('T')[0]) : null,
      },
    });

    // Response validation
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid response format');
    }

    return response.data;
  } catch (error) {
    logError('fetching filtered pays', error);
    throw error;
  }
}

export async function getPaysWithUnpaidTransfers(weekStart, nextWeekStart, bankAccountId = null) {
  // Input validation
  if (!weekStart || isNaN(weekStart.getTime())) {
    throw new Error('Invalid week start date');
  }
  if (!nextWeekStart || isNaN(nextWeekStart.getTime())) {
    throw new Error('Invalid next week start date');
  }

  try {
    // Fetch all pays
    const response = await axiosInstance.get('/pays/');

    // Response validation
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid pays data structure');
    }

    let pays = response.data;

    // Filter pays based on the specified week range
    pays = pays.filter((pay) => {
      const dueDate = new Date(pay.next_pay);
      return dueDate >= weekStart && dueDate < nextWeekStart;
    });

    // Early return if no pays match the date range
    if (pays.length === 0) {
      return { pays: [], totalUnpaidTransferAmount: 0 };
    }

    // Fetch all bank accounts for all pays in a single request with caching
    // Construct a query to get all bank accounts for the filtered pays
    const payIds = pays.map(pay => pay.id);
    // Use caching to reduce redundant API calls
    const bankAccountsPromises = payIds.map(async (payId) => {
      // Check cache first
      const cached = getCachedBankAccounts(payId);
      if (cached) {
        return { data: cached };
      }
      
      // If not cached, fetch from API
      const response = await axiosInstance.get(`/pays/${encodeURIComponent(payId)}/bank-accounts/`);
      
      // Cache the result
      setCachedBankAccounts(payId, response.data);
      
      return response;
    });
    
    const bankAccountsResponses = await Promise.all(bankAccountsPromises);
    
    // Map bank accounts to pays
    pays = pays.map((pay, index) => {
      return {
        ...pay,
        bank_accounts: bankAccountsResponses[index].data
      };
    });

    // Calculate the total unpaid transfer amount for the specified bank account
    let totalUnpaidTransferAmount = 0;
    
    // If bankAccountId is provided, calculate the total for that specific account
    if (bankAccountId) {
      pays.forEach(pay => {
        // Find the bank account entry for this pay
        const bankAccountEntry = pay.bank_accounts.find(
          ba => ba.bank_account === parseInt(bankAccountId, 10) || ba.bank_account === bankAccountId
        );
        
        // If found and not marked as transferred, add to the total
        if (bankAccountEntry && !bankAccountEntry.transfer_made) {
          // Convert to float and add to total
          const transferAmount = parseFloat(bankAccountEntry.transfer_amount) || 0;
          totalUnpaidTransferAmount += transferAmount;
        }
      });
    }

    return { pays, totalUnpaidTransferAmount };
  } catch (error) {
    logError('fetching pays', error);
    throw error;
  }
}


export function calcTransfersTotalFilteredByWeek(pays, weekStart, nextWeekStart) {
  const formattedWeekStart = formatDate(weekStart);
  const formattedNextWeekStart = formatDate(nextWeekStart);
  let totalPayAmount = 0;

  for (const pay of pays) {
    // Only consider the pays that have not yet had the transfer made
    if ((pay.BankAccount.transfer_made === false) && (formattedWeekStart <= pay.due_date) && (pay.due_date < formattedNextWeekStart) && (pay.BankAccount.id === 1)) {
      totalPayAmount += parseFloat(pay.transferAmount);
    }
  }
  return totalPayAmount;
}

export function filterPaysByNextWeek(pays, nextWeekStart, nextFortnightStart) {
  return pays.filter((pay) => {
    const next_pay = new Date(pay.next_pay);
    return nextWeekStart <= next_pay && next_pay < nextFortnightStart;
  }).sort((a, b) => new Date(a.next_pay) - new Date(b.next_pay));
}

export async function fetchPaysInSeries(payId) {
  const idNum = Number(payId);
  if (!Number.isFinite(idNum) || idNum <= 0) {
    throw new Error('Invalid pay ID');
  }

  const response = await axiosInstance.get(`/pays/series/${encodeURIComponent(idNum)}/`);

  if (!response.data) {
    throw new Error('Empty response when fetching pays in series');
  }

  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response.data.pays)) {
    return response.data.pays;
  }

  logError('fetching pays in series', new Error('Unexpected response shape'));
  throw new Error('Unexpected pays-in-series response');
}

export async function updatePayBankAccountForSeries(pay_id, bankAccountId, transferOption, transferAmount) {
  try {
    await updatePayBankAccount(pay_id, bankAccountId, transferOption, transferAmount);
    const paysInSeries = await fetchPaysInSeries(pay_id);

    for (const pay of paysInSeries) {
      await updatePayBankAccount(pay.id, bankAccountId, transferOption, transferAmount);
    }
  } catch (error) {
    logError('updating pay series', error);
    throw error;
  }
}

export async function updateRemainingAmount(payId) {
  // Input validation
  if (!payId || typeof payId !== 'number') {
    throw new Error('Invalid pay ID');
  }

  try {
    const response = await axiosInstance.get(`calculate-remaining-amount/${encodeURIComponent(payId)}/`);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }

    const { remainingAmount, bankAccountId } = response.data;

    return { remainingAmount, bankAccountId };
  } catch (error) {
    logError('updating remaining amount', error);
    throw error;
  }
}

export async function updateSeriesRemainingAmount(payId) {
  // Input validation
  if (!payId || typeof payId !== 'number') {
    throw new Error('Invalid pay ID');
  }

  try {
    const response = await axiosInstance.get(`calc-remaining-for-series/${encodeURIComponent(payId)}/`);

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }

    const { remainingAmount, bankAccountId } = response.data;
    return { remainingAmount, bankAccountId };
  } catch (error) {
    logError('updating remaining amount', error);
    throw error;
  }
}

export async function calculateUnallocatedAmount(payId) {
  // Input validation
  if (!payId || typeof payId !== 'number') {
    debugLog('Invalid pay ID provided to calculateUnallocatedAmount');
    return {}; // Return empty object instead of throwing
  }

  try {
    const response = await axiosInstance.get(`calculate-unallocated-amount/${encodeURIComponent(payId)}/`);

    // Response validation
    if (!response.data) {
      debugLog('Empty response when calculating unallocated amount');
      return {};
    }
    
    if (typeof response.data !== 'object') {
      debugLog('Expected object response but got unexpected data type');
      return {};
    }

    return response.data;
  } catch (error) {
    logError('calculating unallocated amount', error);
    // Return empty object instead of throwing
    return {};
  }
}

export async function updateCanUseRemaining(paySeriesId, canUseRemaining, options = {}) {
  // Input validation
  if (!paySeriesId || (typeof paySeriesId !== 'string' && typeof paySeriesId !== 'number')) {
    throw new Error('Invalid pay series ID');
  }
  if (typeof canUseRemaining !== 'boolean') {
    throw new Error('Invalid canUseRemaining value');
  }

  try {
    const response = await axiosInstance.put(
      `/pays/update-can-use-remaining/${encodeURIComponent(paySeriesId)}/`, {
      canUseRemaining: canUseRemaining,
      ...options,
    });
    return response.data;
  } catch (error) {
    logError('updating canUseRemaining field', error);
    throw error;
  }
}

export async function updateTransferOptionToFixed(paySeriesId) {
  // Input validation
  if (!paySeriesId || (typeof paySeriesId !== 'string' && typeof paySeriesId !== 'number')) {
    throw new Error('Invalid pay series ID');
  }

  try {
    await axiosInstance.put(
      `/pays/update-transfer-option-to-fixed/${encodeURIComponent(paySeriesId)}/`
    );
  } catch (error) {
    logError('updating transfer options', error);
    throw error;
  }
}

export async function updatePayBankAccounts(payId, data) {
  try {
    const response = await axiosInstance.put(`/pays/${payId}/update-bank-accounts/`, data);
    return response.data;
  } catch (error) {
    logError('[PayService] updatePayBankAccounts', error);
    throw error;
  }
}

export async function checkSeriesConflictingUnallocated(payId, bankAccountId) {
  // Input validation
  if (!payId || typeof payId !== 'number') {
    throw new Error('Invalid pay ID');
  }
  if (!bankAccountId || typeof bankAccountId !== 'number') {
    throw new Error('Invalid bank account ID');
  }

  try {
    const response = await axiosInstance.get(
      `/pays/${encodeURIComponent(payId)}/bank-accounts/${encodeURIComponent(bankAccountId)}/check-series-conflicting-unallocated/`
    );
    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid response data');
    }

    return {
      hasConflicts: response.data.has_conflicts,
      conflictingTransfers: response.data.conflicting_transfers || []
    };
  } catch (error) {
    logError('checking for conflicting unallocated transfers', error);
    throw error;
  }
}

export async function getPaysBankAccountsForFortnight(fortnightStart, fortnightEnd, bankAccountId = null) {
  // Input validation
  if (!fortnightStart || isNaN(fortnightStart.getTime())) {
    throw new Error('Invalid fortnight start date');
  }
  if (!fortnightEnd || isNaN(fortnightEnd.getTime())) {
    throw new Error('Invalid fortnight end date');
  }

  try {
    // Fetch all pays in a single request
    const response = await axiosInstance.get('/pays/', {
      params: {
        // Add date range parameters if the API supports it
        start_date: fortnightStart.toISOString().split('T')[0],
        end_date: fortnightEnd.toISOString().split('T')[0]
      }
    });

    // Response validation
    if (!Array.isArray(response.data)) {
      throw new Error('Invalid pays data structure');
    }

    // Filter pays within the fortnight date range
    const paysInFortnight = response.data.filter((pay) => {
      const dueDate = new Date(pay.next_pay);
      return dueDate >= fortnightStart && dueDate <= fortnightEnd;
    });

    if (paysInFortnight.length === 0) {
      return { 
        pays: [], 
        firstWeekPays: [],
        secondWeekPays: [],
        totalUnpaidFirstWeek: 0, 
        totalUnpaidSecondWeek: 0,
        totalUnpaidFortnight: 0 
      };
    }

    // Calculate the start of the second week
    const secondWeekStart = new Date(fortnightStart);
    secondWeekStart.setDate(secondWeekStart.getDate() + 7);

    // Fetch all bank accounts for all pays in a single batch with caching
    const payIds = paysInFortnight.map(pay => pay.id);
    const bankAccountsPromises = payIds.map(async (payId) => {
      // Check cache first
      const cached = getCachedBankAccounts(payId);
      if (cached) {
        return { data: cached };
      }
      
      // If not cached, fetch from API
      const response = await axiosInstance.get(`/pays/${encodeURIComponent(payId)}/bank-accounts/`);
      
      // Cache the result
      setCachedBankAccounts(payId, response.data);
      
      return response;
    });
    
    const bankAccountsResponses = await Promise.all(bankAccountsPromises);
    
    // Map bank accounts to pays
    const paysWithBankAccounts = paysInFortnight.map((pay, index) => {
      return {
        ...pay,
        bank_accounts: bankAccountsResponses[index].data
      };
    });

    // Split pays into first and second week
    const firstWeekPays = paysWithBankAccounts.filter(pay => {
      const dueDate = new Date(pay.next_pay);
      return dueDate >= fortnightStart && dueDate < secondWeekStart;
    });

    const secondWeekPays = paysWithBankAccounts.filter(pay => {
      const dueDate = new Date(pay.next_pay);
      return dueDate >= secondWeekStart && dueDate <= fortnightEnd;
    });

    // Calculate totals if bankAccountId is provided
    let totalUnpaidFirstWeek = 0;
    let totalUnpaidSecondWeek = 0;
    
    if (bankAccountId) {
      // Calculate first week total
      firstWeekPays.forEach(pay => {
        const bankAccountEntry = pay.bank_accounts.find(
          ba => ba.bank_account === parseInt(bankAccountId, 10) || ba.bank_account === bankAccountId
        );
        
        if (bankAccountEntry && !bankAccountEntry.transfer_made) {
          const transferAmount = parseFloat(bankAccountEntry.transfer_amount) || 0;
          totalUnpaidFirstWeek += transferAmount;
        }
      });

      // Calculate second week total
      secondWeekPays.forEach(pay => {
        const bankAccountEntry = pay.bank_accounts.find(
          ba => ba.bank_account === parseInt(bankAccountId, 10) || ba.bank_account === bankAccountId
        );
        
        if (bankAccountEntry && !bankAccountEntry.transfer_made) {
          const transferAmount = parseFloat(bankAccountEntry.transfer_amount) || 0;
          totalUnpaidSecondWeek += transferAmount;
        }
      });
    }

    return {
      pays: paysWithBankAccounts,
      firstWeekPays,
      secondWeekPays,
      totalUnpaidFirstWeek,
      totalUnpaidSecondWeek,
      totalUnpaidFortnight: totalUnpaidFirstWeek + totalUnpaidSecondWeek
    };
  } catch (error) {
    logError('fetching fortnight pays', error);
    throw error;
  }
}

export async function getConsolidatedPayData(bankAccountId, period = 'fortnight_plus_60days', relativeFortnight = 0) {
  // Input validation
  if (!bankAccountId || typeof bankAccountId !== 'number') {
    throw new Error('Invalid bank account ID');
  }
  if (typeof period !== 'string') {
    throw new Error('Invalid period parameter');
  }
  if (typeof relativeFortnight !== 'number') {
    throw new Error('Invalid relative fortnight parameter');
  }

  try {
    const response = await axiosInstance.get(
      `/pays/bank-account/${encodeURIComponent(bankAccountId)}/consolidated/`,
      {
        params: {
          period: period,
          relative_fortnight_index: relativeFortnight
        }
      }
    );

    // Response validation
    if (!response.data || typeof response.data !== 'object') {
      throw new Error('Invalid consolidated pay data response');
    }

    const data = response.data;

    // Validate response structure
    if (!data.bank_account || !data.date_ranges || !data.pays || !data.totals) {
      throw new Error('Invalid consolidated pay data structure');
    }

    // Additional validation for required fields
    if (typeof data.bank_account.id !== 'number' ||
        typeof data.bank_account.nickname !== 'string' ||
        typeof data.bank_account.balance !== 'string') {
      throw new Error('Invalid bank account data in response');
    }

    if (!Array.isArray(data.pays.current_week) ||
        !Array.isArray(data.pays.next_week) ||
        !Array.isArray(data.pays.future) ||
        !Array.isArray(data.pays.all)) {
      throw new Error('Invalid pays data structure in response');
    }

    if (typeof data.totals.unpaid_current_week !== 'number' ||
        typeof data.totals.unpaid_next_week !== 'number' ||
        typeof data.totals.unpaid_fortnight !== 'number') {
      throw new Error('Invalid totals data in response');
    }

    debugSensitive('🚀 Consolidated pay data loaded', {
      totalPays: data.counts.total,
      unpaidFortnightTotal: data.totals.unpaid_fortnight
    });

    return data;
  } catch (error) {
    logError('fetching consolidated pay data', error);
    throw error;
  }
}
